/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle, FiStar, FiArrowRightCircle, FiX, FiEdit } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import {
  deleteTenantPlanIfExpired,
  getTenantPlanId,
  isPlanExpired,
} from "../utils/subscriptionPlan";
// Using colors from useUI instead of hardcoded COLORS

export default function AdminSubscription() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [plans, setPlans] = useState([]);
  const [currentTenantPlan, setCurrentTenantPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const safeString = (val) => {
    if (val === null || val === undefined || typeof val === "object") return "";
    return String(val).trim();
  };

  const trialAlreadyUsed = () =>
    localStorage.getItem("trialUsed") === "1" ||
    Boolean(localStorage.getItem("trialStartDate"));

  // جلب جميع الخطط المتاحة
  const fetchPlans = async () => {
    if (!perms.viewSubscriptions) {
      return;
    }
    try {
      const token = localStorage.getItem("authToken");


      const res = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) throw new Error("Failed to fetch plans");

      const data = await res.json();

      if (Array.isArray(data)) {
        const uniquePlans = [];
        const seenIds = new Set();

        data.forEach((p) => {
          const id = p.planId || p.id || 0;
          if (id && !seenIds.has(id)) {
            seenIds.add(id);
            uniquePlans.push({
              planId: id,
              planType: safeString(p.planType),
              engPlanType: safeString(p.engPlanType),
              price: parseInt(p.price, 10) || 0,
              descripsion: safeString(p.descripsion),
              engDescripsion: safeString(p.engDescripsion),
              isActive: p.isActive ?? 1,
              note1: safeString(p.note1),
              note2: safeString(p.note2),
              note3: safeString(p.note3),
              note4: safeString(p.note4),
              note5: safeString(p.note5),
              note6: safeString(p.note6),
              note7: safeString(p.note7),
              note8: safeString(p.note8),
              engNote1: safeString(p.engNote1),
              engNote2: safeString(p.engNote2),
              engNote3: safeString(p.engNote3),
              engNote4: safeString(p.engNote4),
              engNote5: safeString(p.engNote5),
              engNote6: safeString(p.engNote6),
              engNote7: safeString(p.engNote7),
              engNote8: safeString(p.engNote8),
              creationDate: p.creationDate || "",
            });
          }
        });

        const filteredPlans = trialAlreadyUsed()
          ? uniquePlans.filter((p) => Number(p.price) > 0)
          : uniquePlans;

        setPlans(filteredPlans);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحميل" : "Load Failed",
        text: isArabic ? "فشل تحميل الخطط المتاحة" : "Failed to load available plans",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    }
  };

  // جلب الخطة الحالية للمتجر
  const fetchTenantPlan = async () => {
    if (!perms.viewSubscriptions) {
      return;
    }
    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${baseUrl}/Plans/GetTenantPlan?TenantId=${tenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) {
        // إذا لم يكن هناك خطة حالية، لا بأس
        if (res.status === 404) {
          setCurrentTenantPlan(null);
          return;
        }
        throw new Error("Failed to fetch tenant plan");
      }

      const data = await res.json();
      console.log("📥 GetTenantPlan response:", data);

      // API returns an array, so we need to get the first element
      if (Array.isArray(data) && data.length > 0) {
        const current = data[0];
        if (isPlanExpired(current)) {
          setCurrentTenantPlan(null);
          console.log("⚠️ Current tenant plan expired, hiding it");
          try {
            await deleteTenantPlanIfExpired({
              baseUrl,
              tenantId,
              plan: current,
              token,
            });
          } catch (cleanupErr) {
            console.warn("Could not delete expired tenant plan:", cleanupErr);
          }
        } else {
          setCurrentTenantPlan(current);
          console.log("🔥 current plan after set:", current);

          // حفظ بيانات الاشتراك الجديد محلياً لمنع تكرار رسالة الانتهاء
          const expiryDate =
            current.toDate ||
            current.to ||
            current.ToDate ||
            current.endDate;

          if (expiryDate) {
            localStorage.setItem("plan_expiry_date", expiryDate);
            localStorage.setItem("plan_duration_type", current.planType || current.engPlanType || "Monthly");
            localStorage.removeItem("force_expiry_notice"); // مسح أي علامة إجبارية للتحديث
          }
          console.log("✅ Current tenant plan set:", current);
        }
      } else if (data && typeof data === 'object' && !Array.isArray(data)) {
        // If it's an object (not array), use it directly
        if (isPlanExpired(data)) {
          setCurrentTenantPlan(null);
          console.log("⚠️ Current tenant plan expired, hiding it");
          try {
            await deleteTenantPlanIfExpired({
              baseUrl,
              tenantId,
              plan: data,
              token,
            });
          } catch (cleanupErr) {
            console.warn("Could not delete expired tenant plan:", cleanupErr);
          }
        } else {
          setCurrentTenantPlan(data);
          // حفظ بيانات الاشتراك الجديد محلياً
          const expiryDate = data.toDate || data.to;
          if (expiryDate) {
            localStorage.setItem("plan_expiry_date", expiryDate);
            localStorage.setItem("plan_duration_type", data.planType || data.engPlanType || "Monthly");
            localStorage.removeItem("force_expiry_notice");
          }
          console.log("✅ Current tenant plan set (object):", data);
        }
      } else {
        setCurrentTenantPlan(null);
        console.log("⚠️ No tenant plan found");
      }
    } catch (err) {
      console.error("Error fetching tenant plan:", err);
      setCurrentTenantPlan(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!perms.viewSubscriptions) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await Promise.all([fetchPlans(), fetchTenantPlan()]);
      setLoading(false);
    };
    loadData();
  }, [tenantId, perms.viewSubscriptions]);

  // إعادة جلب البيانات عند العودة من صفحة الدفع (بعد الترقية)
  useEffect(() => {
    // التحقق من وجود علامة نجاح الدفع
    const checkPaymentSuccess = () => {
      const paymentStatus = localStorage.getItem("paymentStatus");
      if (paymentStatus === "success") {
        // إعادة جلب الخطة الحالية
        fetchTenantPlan();
        // تحديث حالة انتهاء الصلاحية فوراً بعد الدفع الناجح
        localStorage.removeItem("force_expiry_notice");
        // مسح العلامة
        localStorage.removeItem("paymentStatus");
      }
    };

    // التحقق عند تحميل الصفحة
    checkPaymentSuccess();

    // التحقق عند العودة للصفحة
    const handleFocus = () => {
      checkPaymentSuccess();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // ترقية/تغيير الخطة - توجيه إلى صفحة الدفع
  const handleUpgrade = (plan) => {
    // حفظ بيانات الخطة المختارة في localStorage للترقية
    localStorage.setItem("upgradePlanId", plan.planId.toString());
    localStorage.setItem("upgradePlanType", plan.planType || "");
    localStorage.setItem("upgradeEngPlanType", plan.engPlanType || "");
    localStorage.setItem("upgradePlanPrice", (plan.price || 0).toString());
    localStorage.setItem("upgradePlanDescription", plan.descripsion || "");
    localStorage.setItem("upgradeEngPlanDescription", plan.engDescripsion || "");
    localStorage.setItem("upgradePlanNote1", plan.note1 || "");
    localStorage.setItem("upgradePlanNote2", plan.note2 || "");
    localStorage.setItem("upgradePlanNote3", plan.note3 || "");
    localStorage.setItem("upgradePlanNote4", plan.note4 || "");
    localStorage.setItem("upgradePlanNote5", plan.note5 || "");
    localStorage.setItem("upgradePlanNote6", plan.note6 || "");
    localStorage.setItem("upgradePlanNote7", plan.note7 || "");
    localStorage.setItem("upgradePlanNote8", plan.note8 || "");
    localStorage.setItem("upgradeEngPlanNote1", plan.engNote1 || "");
    localStorage.setItem("upgradeEngPlanNote2", plan.engNote2 || "");
    localStorage.setItem("upgradeEngPlanNote3", plan.engNote3 || "");
    localStorage.setItem("upgradeEngPlanNote4", plan.engNote4 || "");
    localStorage.setItem("upgradeEngPlanNote5", plan.engNote5 || "");
    localStorage.setItem("upgradeEngPlanNote6", plan.engNote6 || "");
    localStorage.setItem("upgradeEngPlanNote7", plan.engNote7 || "");
    localStorage.setItem("upgradeEngPlanNote8", plan.engNote8 || "");
    localStorage.setItem("upgradePlanCreationDate", plan.creationDate || "");
    localStorage.setItem("isUpgrade", "true"); // علامة أن هذه ترقية

    // ✅ تحديث المفاتيح الأساسية أيضاً لضمان التوافق مع بقية التطبيق (WhatsLayout & PaymentPage)
    localStorage.setItem("selectedPlanId", plan.planId.toString());
    localStorage.setItem("selectedPlanPrice", (plan.price || 0).toString());
    localStorage.setItem("selectedPlanType", plan.planType || plan.engPlanType || "");
    localStorage.setItem("selectedEngPlanType", plan.engPlanType || plan.planType || "");
    localStorage.setItem("selectedPlanDesc", plan.descripsion || "");
    localStorage.setItem("selectedPlanEngDesc", plan.engDescripsion || "");
    
    // مسح علامات الانتهاء فور اختيار خطة جديدة للبدأ في عملية الدفع أو الترقية
    localStorage.removeItem("force_expiry_notice");
    sessionStorage.removeItem("trialExpiredAlertShown");
    sessionStorage.removeItem("paidExpiredAlertShown");
    
    // وضع علامة تجديد فورية لمنع الطرد من القالب الرئيسي
    const now = new Date();
    localStorage.setItem("plan_renewed_at", now.toISOString());
    
    // حساب تاريخ انتهاء تقريبي (سيتم تحديثه لاحقاً من الـ API)
    const isFree = !plan.price || plan.price === 0;
    const expiryDate = new Date(now.getTime() + (isFree ? 7 : 30) * 24 * 60 * 60 * 1000);
    localStorage.setItem("plan_expiry_date", expiryDate.toISOString());
    
    // توجيه إلى صفحة الدفع
    navigate("/payment?upgrade=true");
  };

  // إيقاف الاشتراك
  const handleCancel = async () => {
    if (!perms.editSubscriptions) {
      return;
    }
    if (!currentTenantPlan) return;

    const result = await Swal.fire({
      icon: "warning",
      title: isArabic ? "إيقاف الاشتراك" : "Cancel Subscription",
      text: isArabic
        ? "هل أنت متأكد من إيقاف الاشتراك؟ سيتم إلغاء الخطة الحالية."
        : "Are you sure you want to cancel your subscription? Your current plan will be cancelled.",
      showCancelButton: true,
      confirmButtonColor: colors.red,
      cancelButtonColor: "#6b7280",
      confirmButtonText: isArabic ? "نعم، إيقاف" : "Yes, Cancel",
      cancelButtonText: isArabic ? "إلغاء" : "No",
      background: isDark ? "#1E293B" : "#fff",
      color: isDark ? "#fff" : "#000",
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");


      // استخراج planId من currentTenantPlan مع التحقق من جميع الحالات الممكنة
      // أولاً من currentTenantPlan مباشرة
      let planId = getTenantPlanId(currentTenantPlan);

      // إذا لم نجد planId، نبحث في plans array
      if (!planId || planId === 0) {
        const currentPlanId = getTenantPlanId(currentTenantPlan);
        const foundPlan = plans.find(p => p.planId === currentPlanId);
        if (foundPlan) {
          planId = foundPlan.planId;
        }
      }

      // إذا لم نجد planId بعد، نستخدم 0
      if (!planId || planId === 0) {
        planId = 0;
      }

      // التحقق من أن tenantId و planId صحيحين
      if (!tenantId || tenantId === -1) {
        throw new Error(isArabic ? "معرف المتجر غير صحيح" : "Invalid tenant ID");
      }

      if (!planId || planId === 0) {
        throw new Error(isArabic ? "معرف الخطة غير صحيح" : "Invalid plan ID");
      }

      console.log("🗑️ Cancelling subscription:", { tenantId, planId, currentTenantPlan, currentPlanData });

      const res = await fetch(`${baseUrl}/Plans/DeleteTenantPlan?TenantId=${tenantId}&PlanId=${planId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.Message || `HTTP ${res.status}: ${res.statusText}`;
        console.error("❌ DeleteTenantPlan error:", errorData);
        throw new Error(errorMessage);
      }

      const responseData = await res.json().catch(() => ({}));
      console.log("✅ Subscription cancelled successfully:", responseData);

      setCurrentTenantPlan(null);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الإيقاف! ✅" : "Cancelled! ✅",
        text: isArabic ? "تم إيقاف الاشتراك بنجاح" : "Subscription cancelled successfully",
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("Error cancelling subscription:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الإيقاف" : "Cancel Failed",
        text: err.message || (isArabic ? "فشل إيقاف الاشتراك" : "Failed to cancel subscription"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally {
      setSaving(false);
    }
  };

  // تحديد إذا كانت الخطة هي الخطة الحالية
  const isCurrentPlan = (plan) => {
    if (!currentTenantPlan || isPlanExpired(currentTenantPlan)) return false;
    const currentPlanId = getTenantPlanId(currentTenantPlan);
    return plan.planId === currentPlanId;
  };

  // استخراج المميزات من notes
  const getFeatures = (plan) => {
    const features = [];
    for (let i = 1; i <= 8; i++) {
      const note = isArabic ? plan[`note${i}`] : plan[`engNote${i}`];
      if (note && note.trim()) {
        features.push(note);
      }
    }
    return features.length > 0 ? features : [
      isArabic ? "لا توجد مميزات محددة" : "No specific features"
    ];
  };

  if (!perms.viewSubscriptions) {
    return <NoAccess />;
  }
  if (loading) {
    return (
      <WhatsLayout title={isArabic ? "الاشتراك" : "Subscription"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
            <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
              {isArabic ? "جاري التحميل..." : "Loading..."}
            </p>
          </div>
        </div>
      </WhatsLayout>
    );
  }

  // الحصول على بيانات الخطة الحالية من plans array
  const getCurrentPlanData = () => {
    if (!currentTenantPlan || isPlanExpired(currentTenantPlan)) return null;
    // استخراج planId من currentTenantPlan (قد يكون planId أو planid)
    const currentPlanId = getTenantPlanId(currentTenantPlan);

    console.log("🔍 Looking for plan with ID:", currentPlanId, "in plans:", plans.map(p => p.planId));

    // البحث في plans array عن الخطة المطابقة
    const foundPlan = plans.find(p => p.planId === currentPlanId);

    if (foundPlan) {
      console.log("✅ Found matching plan in plans array:", foundPlan);
      return foundPlan;
    }

    // إذا لم نجدها في plans، نستخدم بيانات currentTenantPlan مباشرة
    console.log("⚠️ Plan not found in plans array, using currentTenantPlan data:", currentTenantPlan);
    return currentTenantPlan;
  };

  // الحصول على الخطط المتاحة للترقية (غير الحالية)
  const getAvailablePlans = () => {
    const filteredBase = trialAlreadyUsed() ? plans.filter((p) => Number(p.price) > 0) : plans;
    if (!currentTenantPlan || isPlanExpired(currentTenantPlan)) return filteredBase;
    const currentPlanId = getTenantPlanId(currentTenantPlan);
    return filteredBase.filter(p => p.planId !== currentPlanId);
  };

  const expiredCurrentPlan = currentTenantPlan && isPlanExpired(currentTenantPlan);
  const currentPlanData = expiredCurrentPlan ? null : getCurrentPlanData();
  const displayCurrentPlan = currentPlanData || currentTenantPlan;
  const availablePlans = getAvailablePlans();

  // استخراج التواريخ بشكل آمن
  const startDateRaw =
    currentTenantPlan?.fromDate ||
    currentTenantPlan?.FromDate ||
    currentTenantPlan?.from ||
    currentTenantPlan?.startDate ||
    currentTenantPlan?.StartDate ||
    currentTenantPlan?.creationDate;

let expiryDateRaw =
    currentTenantPlan?.toDate ||
    currentTenantPlan?.ToDate ||
    currentTenantPlan?.to ||
    currentTenantPlan?.To ||
    currentTenantPlan?.endDate ||
    currentTenantPlan?.EndDate ||
    currentTenantPlan?.expiryDate ||
    currentTenantPlan?.ExpiryDate;

  // تنظيف السلسلة النصية للتاريخ لضمان التحليل الصحيح (مثلاً معالجة المسافات الزائدة Mar 28 2026  4:48AM)
  // تنظيف السلسلة النصية للتاريخ لضمان التحليل الصحيح
  const cleanDateStr = (val) => {
    if (!val) return val;
    if (val instanceof Date) return val;
    return String(val).replace(/\s+/g, " ").trim();
  };

  // محاولة قراءة تاريخ البداية أو استخدام "الآن" كخلفية بديلة لضمان عدم حدوث null
  let startDate = startDateRaw ? new Date(cleanDateStr(startDateRaw)) : new Date();
  if (isNaN(startDate.getTime())) {
    startDate = new Date(); // إذا كان التنسيق غير صالح، نفترض أن البداية هي اليوم
  }

  // التحقق مما إذا كان تاريخ الانتهاء مفقوداً أو غير صالح
  const isExpiryInvalid = !expiryDateRaw || 
                          expiryDateRaw === "undefined" || 
                          isNaN(new Date(cleanDateStr(expiryDateRaw)).getTime());

  // ✅ الحساب التلقائي الإجباري: يعتمد على السعر (شهر للقيمة، أسبوع للصفر)
  if (isExpiryInvalid) {
    const planPrice = Number(currentTenantPlan?.price || 0);
    const daysToAdd = planPrice > 0 ? 30 : 7;

    const calculatedDate = new Date(startDate.getTime());
    calculatedDate.setDate(calculatedDate.getDate() + daysToAdd);
    expiryDateRaw = calculatedDate;
  }

  const expiryDate = expiryDateRaw ? new Date(cleanDateStr(expiryDateRaw)) : null;

  const now = new Date();

  // الأيام المتبقية
  const remainingDays =
    expiryDate && !isNaN(expiryDate)
      ? Math.max(0, Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)))
      : 0;

  // progress
  const totalDuration =
    startDate && expiryDate ? expiryDate - startDate : 0;

  const timePassed =
    startDate ? now - startDate : 0;

  const progressPercent =
    totalDuration > 0
      ? Math.max(0, Math.min(100, (timePassed / totalDuration) * 100))
      : 0;

  const remainingPercent = 100 - progressPercent;

  console.log({
    startDateRaw,
    expiryDateCalculated: expiryDate,
    currentTenantPlan
  });

  return (
    <WhatsLayout title={isArabic ? "الاشتراك" : "Subscription"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-14 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : colors.bg,
        }}
      >
        {expiredCurrentPlan && (
          <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 font-semibold">
            {isArabic
              ? "اشتراكك الحالي منتهي، يرجى تجديد الاشتراك أو اختيار خطة جديدة."
              : "Your current subscription has expired. Please renew or choose a new plan."}
          </div>
        )}

        {/* ===== الخطة الحالية ===== */}
        {displayCurrentPlan && perms.viewSubscriptions && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-10"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
                {expiredCurrentPlan
                  ? (isArabic ? "خطتك منتهية" : "Your Plan Has Expired")
                  : (isArabic ? "خطتك الحالية" : "Your Current Plan")}
              </h2>
            </div>

            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <motion.div
                  whileHover={{ y: -8, scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className={`relative p-8 rounded-3xl shadow-2xl backdrop-blur-md border transition-all duration-300 ${isDark
                    ? "bg-[#1E293B]/70 border-[#334155]"
                    : "bg-white/80 border-gray-200"
                    } ring-4 ${expiredCurrentPlan ? "ring-amber-400 shadow-amber-500/30" : "ring-green-400 shadow-green-500/30"}`}
                >
                  {/* شعار الخطة الحالية */}
                  <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 text-white text-sm font-semibold px-4 py-1 rounded-full shadow-md z-10 ${expiredCurrentPlan ? "bg-amber-500" : "bg-green-500"}`}>
                    <FiStar className="text-yellow-300" />
                    {expiredCurrentPlan
                      ? (isArabic ? "الخطة منتهية" : "Expired Plan")
                      : (isArabic ? "الخطة الحالية" : "Current Plan")}
                  </div>

                  {/* عنوان الخطة */}
                  <h3
                    className="text-2xl font-bold mb-2 text-center mt-2"
                    style={{ color: expiredCurrentPlan ? colors.red : colors.green }}
                  >
                    {isArabic
                      ? (displayCurrentPlan.planType || displayCurrentPlan.engPlanType)
                      : (displayCurrentPlan.engPlanType || displayCurrentPlan.planType)}
                  </h3>

                  {/* السعر */}
                  <p
                    className="text-xl font-semibold mb-5 text-center"
                    style={{ color: expiredCurrentPlan ? colors.red : colors.green }}
                  >
                    {formatNumber(displayCurrentPlan.price)} {isArabic ? "د.ع / شهر" : "IQD / mo"}
                  </p>

                  {/* عرض تاريخ التجديد القادم (متى ستظهر الرسالة مرة أخرى) */}
                  {startDate && expiryDate && (
                    <div className="mb-6">
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 rounded-2xl bg-purple-500/5 border border-purple-500/10 text-center">
                          <p className="text-[10px] opacity-60 font-bold uppercase tracking-widest">
                            {isArabic ? "تاريخ الاشتراك" : "Start Date"}
                          </p>
                          <p className="text-sm font-black text-purple-600 dark:text-purple-400 mt-1">
                            {startDate?.toLocaleDateString(isArabic ? "ar-EG" : "en-US")}
                          </p>
                        </div>

                        <div className="p-3 rounded-2xl bg-blue-500/5 border border-blue-500/10 text-center">
                          <p className="text-[10px] opacity-60 font-bold uppercase tracking-widest">{isArabic ? "تاريخ التجديد" : "Renewal Date"}</p>
                          <p className="text-sm font-black text-blue-600 dark:text-blue-400 mt-1">
                            {expiryDate?.toLocaleDateString(isArabic ? 'ar-EG' : 'en-US')}
                          </p>
                        </div>

                        <div className={`p-3 rounded-2xl border text-center ${remainingDays <= 3 ? "bg-red-500/10 border-red-500/20 text-red-600" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"}`}>
                          <p className="text-[10px] opacity-60 font-bold uppercase tracking-widest">{isArabic ? "الأيام المتبقية" : "Days Left"}</p>
                          <p className="text-sm font-black mt-1">{remainingDays} {isArabic ? "يوم" : "Days"}</p>
                        </div>
                      </div>

                      {/* شريط العداد التنازلي المرئي */}
                      <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden border border-gray-200 dark:border-gray-700">
                        <motion.div
                          initial={{ width: "100%" }}
                          animate={{ width: `${progressPercent}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className={`h-full ${remainingDays <= 3 ? "bg-red-500" : "bg-emerald-500"}`}
                        />
                      </div>
                      <p className="text-[9px] mt-1 opacity-50 text-center uppercase font-bold tracking-tighter">
                        {isArabic ? "شريط حالة مدة الاشتراك" : "Subscription duration status bar"}
                      </p>
                    </div>
                  )}


                  {/* المميزات */}
                  <ul className="space-y-3 mb-8">
                    {getFeatures(displayCurrentPlan).map((f, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm leading-relaxed"
                        style={{ color: isDark ? "#e2e8f0" : "#475569" }}
                      >
                        <FiCheckCircle className={`${expiredCurrentPlan ? "text-amber-500" : "text-green-500"} text-lg flex-shrink-0`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {expiredCurrentPlan ? (
                    <motion.button
                      onClick={() => navigate("/admin-store/subscriptions")}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full py-3 rounded-xl text-white font-semibold shadow-lg transition flex items-center justify-center gap-2"
                      style={{ background: colors.red }}
                    >
                      <FiX size={18} />
                      {isArabic ? "تجديد الاشتراك" : "Renew Subscription"}
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={perms.editSubscriptions ? handleCancel : null}
                      disabled={saving}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full py-3 rounded-xl text-white font-semibold shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ background: colors.red }}
                    >
                      <FiX size={18} />
                      {isArabic ? "إلغاء الاشتراك" : "Cancel Subscription"}
                    </motion.button>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ===== الخطط المتاحة للترقية ===== */}
        {availablePlans.length > 0 && perms.viewSubscriptions && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <h2 className="text-2xl font-bold mb-6" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
              {isArabic ? "خطط متاحة للترقية" : "Available Plans for Upgrade"}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
              {availablePlans.map((plan) => {
                const features = getFeatures(plan);
                const planName = isArabic ? (plan.planType || plan.engPlanType) : (plan.engPlanType || plan.planType);
                const planPrice = `${formatNumber(plan.price)} ${isArabic ? "د.ع / شهر" : "IQD / mo"}`;

                // تحديد اللون حسب ترتيب الخطة
                const planColors = [colors.mainColor, colors.gold, "#64748B"];
                const planColor = planColors[(plan.planId - 1) % planColors.length] || colors.mainColor;

                return (
                  <motion.div
                    key={plan.planId}
                    whileHover={{ y: -8, scale: 1.03 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className={`relative p-8 rounded-3xl shadow-2xl backdrop-blur-md border transition-all duration-300 ${isDark
                      ? "bg-[#1E293B]/70 border-[#334155]"
                      : "bg-white/80 border-gray-200"
                      }`}
                  >
                    {/* عنوان الخطة */}
                    <h3
                      className="text-2xl font-bold mb-2 text-center"
                      style={{ color: planColor }}
                    >
                      {planName}
                    </h3>

                    {/* السعر */}
                    <p
                      className="text-xl font-semibold mb-5 text-center"
                      style={{ color: colors.mainColor }}
                    >
                      {planPrice}
                    </p>

                    {/* المميزات */}
                    <ul className="space-y-3 mb-8">
                      {features.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm leading-relaxed"
                          style={{ color: isDark ? "#e2e8f0" : "#475569" }}
                        >
                          <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* زر الترقية */}
                    <button
                      disabled={saving}
                      onClick={perms.editSubscriptions ? () => handleUpgrade(plan) : null}
                      className="w-full py-3 rounded-xl font-semibold text-white flex justify-center items-center gap-2 transition-all duration-200 shadow-md"
                      style={{
                        background: `linear-gradient(90deg, ${planColor}, ${colors.green})`,
                      }}
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {isArabic ? "جاري المعالجة..." : "Processing..."}
                        </>
                      ) : (
                        <>
                          <FiArrowRightCircle />
                          {isArabic ? "ترقية الآن" : "Upgrade Now"}
                        </>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* حالة عدم وجود خطط */}
        {!currentPlanData && plans.length === 0 && perms.viewSubscriptions && (
          <div className="text-center py-12">
            <p style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
              {isArabic ? "لا توجد خطط متاحة" : "No plans available"}
            </p>
          </div>
        )}

        {/* حالة عدم وجود خطة حالية ولكن هناك خطط متاحة */}
        {!currentPlanData && plans.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-2xl font-bold mb-6" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
              {isArabic ? "اختر خطة" : "Choose a Plan"}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
              {plans.map((plan) => {
                const features = getFeatures(plan);
                const planName = isArabic ? (plan.planType || plan.engPlanType) : (plan.engPlanType || plan.planType);
                const planPrice = `${formatNumber(plan.price)} ${isArabic ? "د.ع / شهر" : "IQD / mo"}`;

                const planColors = [colors.mainColor, colors.gold, "#64748B"];
                const planColor = planColors[(plan.planId - 1) % planColors.length] || colors.mainColor;

                return (
                  <motion.div
                    key={plan.planId}
                    whileHover={{ y: -8, scale: 1.03 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className={`relative p-8 rounded-3xl shadow-2xl backdrop-blur-md border transition-all duration-300 ${isDark
                      ? "bg-[#1E293B]/70 border-[#334155]"
                      : "bg-white/80 border-gray-200"
                      }`}
                  >
                    <h3
                      className="text-2xl font-bold mb-2 text-center"
                      style={{ color: planColor }}
                    >
                      {planName}
                    </h3>
                    <p
                      className="text-xl font-semibold mb-5 text-center"
                      style={{ color: colors.mainColor }}
                    >
                      {planPrice}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {features.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm leading-relaxed"
                          style={{ color: isDark ? "#e2e8f0" : "#475569" }}
                        >
                          <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      disabled={saving}
                      onClick={perms.editSubscriptions ? () => handleUpgrade(plan) : null}
                      className="w-full py-3 rounded-xl font-semibold text-white flex justify-center items-center gap-2 transition-all duration-200 shadow-md"
                      style={{
                        background: `linear-gradient(90deg, ${planColor}, ${colors.green})`,
                      }}
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {isArabic ? "جاري المعالجة..." : "Processing..."}
                        </>
                      ) : (
                        <>
                          <FiArrowRightCircle />
                          {isArabic ? "اختر هذه الخطة" : "Choose This Plan"}
                        </>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {!currentPlanData && plans.length > 0 && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 font-semibold">
            {isArabic
              ? "لا توجد خطة حالية فعالة. اختر خطة جديدة للمتابعة."
              : "No active plan found. Please choose a new plan to continue."}
          </div>
        )}
      </section>
    </WhatsLayout>
  );
}
